import React, { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries, LineSeries, HistogramSeries, AreaSeries, createSeriesMarkers } from "lightweight-charts";
import { useTheme } from "../contexts/ThemeContext";
import * as ind from "../lib/indicators";

export default function CandleChart({ bars, ema10, entry, stopLoss, target, height = 440, cupOverlay, emaCrossOverlay, goldenCrossOverlay, niftyBars, sectorBars, sectorName, indicators, indicatorParams, symbol, companyName, srLevels, onReady }) {
  const ref = useRef(null);
  const { theme } = useTheme();
  // TradingView-style OHLCV legend in the top-left of the chart. Updates
  // as the crosshair moves over candles; falls back to the latest bar
  // when the user is not hovering. Stored as a small object so React
  // re-renders only the legend (not the whole chart).
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (!ref.current || !bars?.length) return;
    const el = ref.current;
    // Safe: clears DOM children, no HTML parsing.
    el.replaceChildren();

    const isLight = theme === "light";
    const palette = isLight
      ? {
          bg: "#ffffff",
          text: "#111111",
          grid: "#ececec",
          border: "#dcdcdc",
          up: "#10a37f",
          down: "#dc2626",
          volume: "rgba(100,100,120,0.25)",
        }
      : {
          bg: "#0a0a0c",
          text: "#e5e5e5",
          grid: "#1f1f25",
          border: "#28282f",
          up: "#10b981",
          down: "#ef4444",
          volume: "rgba(160,160,180,0.25)",
        };

    const chart = createChart(el, {
      height,
      width: el.clientWidth,
      layout: {
        background: { color: palette.bg },
        textColor: palette.text,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 11,
      },
      localization: { locale: "en-US" },
      grid: {
        vertLines: { color: palette.grid },
        horzLines: { color: palette.grid },
      },
      rightPriceScale: { borderColor: palette.border },
      timeScale: { borderColor: palette.border, timeVisible: false, secondsVisible: false },
      crosshair: { mode: 1 },
    });

    const candle = chart.addSeries(CandlestickSeries, {
      upColor: palette.up,
      downColor: palette.down,
      borderUpColor: palette.up,
      borderDownColor: palette.down,
      wickUpColor: palette.up,
      wickDownColor: palette.down,
    });
    candle.setData(bars);

    if (ema10 && ema10.length && !emaCrossOverlay && !goldenCrossOverlay) {
      // Skip the default backend-provided EMA10 series when we're in
      // ema-crossover overlay mode — that mode renders its own EMA10
      // (cyan) and EMA20 (magenta) for visual consistency. Two EMA10
      // lines at the same price would just visually clash.
      const emaSeries = chart.addSeries(LineSeries, {
        color: "#f59e0b",
        lineWidth: 1.5,
        priceLineVisible: false,
        title: "EMA 10",
      });
      emaSeries.setData(ema10);
    }

    // -----------------------------------------------------------------
    // EMA 10 / 20 crossover overlay
    // -----------------------------------------------------------------
    // When the modal is rendering a stock opened from the EMA Crossover
    // scanner tab we draw BOTH the 10-EMA (cyan) and 20-EMA (magenta)
    // computed from the visible bars, plus an arrow marker at the exact
    // bar where the 10 crosses above (golden) or below (death) the 20.
    // This makes it visually obvious WHY this stock made the list.
    // -----------------------------------------------------------------
    if (emaCrossOverlay && bars.length >= 21) {
      const emaCalc = (period) => {
        const k = 2 / (period + 1);
        const out = [];
        let prev = null;
        for (let i = 0; i < bars.length; i += 1) {
          const c = bars[i].close;
          prev = prev == null ? c : c * k + prev * (1 - k);
          out.push({ time: bars[i].time, value: prev });
        }
        return out;
      };
      const e10 = emaCalc(10);
      const e20 = emaCalc(20);
      const s10 = chart.addSeries(LineSeries, {
        color: "#06b6d4",   // cyan
        lineWidth: 2,
        priceLineVisible: false,
        title: "EMA 10",
      });
      s10.setData(e10);
      const s20 = chart.addSeries(LineSeries, {
        color: "#d946ef",   // magenta
        lineWidth: 2,
        priceLineVisible: false,
        title: "EMA 20",
      });
      s20.setData(e20);

      // Find the most recent crossover bar (last 20 bars window)
      const startIdx = Math.max(1, bars.length - 20);
      let crossIdx = -1;
      let crossDir = null; // "long" / "short"
      for (let i = bars.length - 1; i >= startIdx; i -= 1) {
        const prevDiff = e10[i - 1].value - e20[i - 1].value;
        const curDiff = e10[i].value - e20[i].value;
        if (prevDiff <= 0 && curDiff > 0) { crossIdx = i; crossDir = "long"; break; }
        if (prevDiff >= 0 && curDiff < 0) { crossIdx = i; crossDir = "short"; break; }
      }
      if (crossIdx > 0) {
        const m = [{
          time: bars[crossIdx].time,
          position: crossDir === "long" ? "belowBar" : "aboveBar",
          color: crossDir === "long" ? palette.up : palette.down,
          shape: crossDir === "long" ? "arrowUp" : "arrowDown",
          text: crossDir === "long" ? "10 ↗ 20" : "10 ↘ 20",
        }];
        createSeriesMarkers(candle, m);
      }
    }

    // -----------------------------------------------------------------
    // Golden Cross overlay — EMA 50 (blue) + EMA 200 (amber) +
    // crossover marker (last 60 bars window for the slower pair).
    // -----------------------------------------------------------------
    if (goldenCrossOverlay && bars.length >= 50) {
      const emaCalcG = (period) => {
        const k = 2 / (period + 1);
        const out = [];
        let prev = null;
        for (let i = 0; i < bars.length; i += 1) {
          const c = bars[i].close;
          prev = prev == null ? c : c * k + prev * (1 - k);
          out.push({ time: bars[i].time, value: prev });
        }
        return out;
      };
      const e50 = emaCalcG(50);
      const e200 = bars.length >= 50 ? emaCalcG(200) : null;
      const s50 = chart.addSeries(LineSeries, {
        color: "#3b82f6",
        lineWidth: 2,
        priceLineVisible: false,
        title: "EMA 50",
      });
      s50.setData(e50);
      if (e200) {
        const s200 = chart.addSeries(LineSeries, {
          color: "#f59e0b",
          lineWidth: 2,
          priceLineVisible: false,
          title: "EMA 200",
        });
        s200.setData(e200);
        const startIdx = Math.max(1, bars.length - 60);
        let crossIdx = -1;
        for (let i = bars.length - 1; i >= startIdx; i -= 1) {
          const prevDiff = e50[i - 1].value - e200[i - 1].value;
          const curDiff = e50[i].value - e200[i].value;
          if (prevDiff <= 0 && curDiff > 0) { crossIdx = i; break; }
        }
        if (crossIdx > 0) {
          createSeriesMarkers(candle, [{
            time: bars[crossIdx].time,
            position: "belowBar",
            color: palette.up,
            shape: "arrowUp",
            text: "50 ↗ 200",
          }]);
        }
      }
    }

    // Horizontal reference price lines
    const addLine = (price, color, title, dashed = false) => {
      if (price == null) return;
      candle.createPriceLine({
        price,
        color,
        lineWidth: 1,
        lineStyle: dashed ? 2 : 0, // 0 solid, 2 dashed
        axisLabelVisible: true,
        title,
      });
    };
    addLine(entry, palette.up, "Entry", false);
    addLine(stopLoss, palette.down, "SL", true);
    addLine(target, palette.up, "Target", true);

    // -----------------------------------------------------------------
    // Today's % move marker — circuit-locked stocks (O=H=L=C) render
    // as a 1-pixel doji that's easy to miss. Add a chart marker with
    // the day's % change above (gainers) or below (losers) the last
    // bar whenever today moved ≥ 4%.
    // -----------------------------------------------------------------
    if (bars.length >= 2) {
      const last = bars[bars.length - 1];
      const prev = bars[bars.length - 2];
      if (prev?.close > 0) {
        const pct = ((last.close - prev.close) / prev.close) * 100;
        const absPct = Math.abs(pct);
        if (absPct >= 4) {
          const up = pct >= 0;
          createSeriesMarkers(candle, [{
            time: last.time,
            position: up ? "aboveBar" : "belowBar",
            color: up ? palette.up : palette.down,
            shape: up ? "arrowUp" : "arrowDown",
            text: `${up ? "+" : ""}${pct.toFixed(1)}%`,
          }]);
        }
      }
    }

    // -----------------------------------------------------------------
    // AI-detected Support / Resistance overlay — light dashed lines
    // with 70% transparency. Lifecycle: cleared on every chart re-render
    // (clean slate from createChart cleanup above) so toggling the
    // analysis panel off automatically wipes them.
    // -----------------------------------------------------------------
    if (Array.isArray(srLevels) && srLevels.length > 0) {
      const hexAlpha = "4D"; // ~30% alpha → 70% transparent
      srLevels.forEach((lv) => {
        if (!lv || typeof lv.price !== "number") return;
        const baseColor = lv.kind === "support" ? palette.up : palette.down;
        const color = baseColor.startsWith("#") && baseColor.length === 7
          ? baseColor + hexAlpha
          : baseColor;
        const strength = (lv.strength || "Weak").toLowerCase();
        const lineWidth = strength === "strong" ? 2 : 1;
        candle.createPriceLine({
          price: lv.price,
          color,
          lineWidth,
          lineStyle: 2, // dashed
          axisLabelVisible: true,
          title: `${lv.pinned ? "★ " : ""}${lv.kind === "support" ? "S" : "R"} · ${lv.strength?.[0] || ""}`,
        });
      });
    }

    // -----------------------------------------------------------------
    // Nifty 50 normalised comparison overlay
    // ------------------------------------------------------------------
    // When `niftyBars` is provided we render an extra line on a separate
    // price scale showing Nifty 50's % move from the same start point as
    // the stock — so the trader can visually judge relative strength
    // (above the line = outperforming the index, below = lagging).
    // ------------------------------------------------------------------
    if (Array.isArray(niftyBars) && niftyBars.length > 1 && bars.length > 0) {
      // Sync Nifty to stock's first visible bar timestamp
      const stockStart = bars[0].time;
      const stockStartClose = bars[0].close;
      const trimmed = niftyBars.filter((b) => b.time >= stockStart);
      if (trimmed.length > 1) {
        const niftyStart = trimmed[0].close;
        // Rebase Nifty to start at the stock's first close (so the two
        // lines visually start from the same point regardless of price scale)
        const series = chart.addSeries(LineSeries, {
          color: "#fbbf24",   // amber
          lineWidth: 2,
          lineStyle: 2,        // dashed
          priceLineVisible: false,
          lastValueVisible: true,
          title: "Nifty 50",
        });
        series.setData(
          trimmed.map((b) => ({
            time: b.time,
            value: Number(((b.close / niftyStart) * stockStartClose).toFixed(2)),
          })),
        );
      }
    }

    // -----------------------------------------------------------------
    // Sector index normalised overlay (magenta dashed). Same rebase trick:
    // anchor first close to the stock's first close so all three lines
    // share visual price space. Only rendered when `sectorBars` provided.
    // -----------------------------------------------------------------
    if (Array.isArray(sectorBars) && sectorBars.length > 1 && bars.length > 0) {
      const stockStart = bars[0].time;
      const stockStartClose = bars[0].close;
      const trimmed = sectorBars.filter((b) => b.time >= stockStart);
      if (trimmed.length > 1) {
        const secStart = trimmed[0].close;
        const series = chart.addSeries(LineSeries, {
          color: "#d946ef",   // magenta
          lineWidth: 2,
          lineStyle: 2,        // dashed
          priceLineVisible: false,
          lastValueVisible: true,
          title: sectorName || "Sector",
        });
        series.setData(
          trimmed.map((b) => ({
            time: b.time,
            value: Number(((b.close / secStart) * stockStartClose).toFixed(2)),
          })),
        );
      }
    }

    // -----------------------------------------------------------------
    // Cup-and-Handle visual overlay
    // ------------------------------------------------------------------
    // When the modal is rendering a Cup setup we overlay the geometry
    // directly on the chart: a dashed line for the rim level (resistance),
    // another dashed line for the cup low, and 4 markers labelling the
    // left rim / cup low / right rim / breakout days. This makes the
    // setup self-explanatory — the trader can visually confirm the U-shape
    // without re-reading the geometry strip on the card.
    // ------------------------------------------------------------------
    if (cupOverlay) {
      addLine(cupOverlay.rim, "#a855f7", "Rim", true);   // purple
      addLine(cupOverlay.cupLow, "#0ea5e9", "Cup low", true); // sky blue

      const markers = [];
      const push = (date, color, position, shape, text) => {
        if (!date) return;
        const t = Math.floor(new Date(date).getTime() / 1000);
        if (!Number.isFinite(t) || t <= 0) return;
        markers.push({ time: t, position, color, shape, text });
      };
      // `belowBar` keeps the marker visually associated with the bar's low
      push(cupOverlay.leftRimDate, "#a855f7", "aboveBar", "arrowDown", "Left rim");
      push(cupOverlay.cupLowDate, "#0ea5e9", "belowBar", "circle", "Cup low");
      push(cupOverlay.rightRimDate, "#a855f7", "aboveBar", "arrowDown", "Right rim");
      push(cupOverlay.breakoutDate, palette.up, "belowBar", "arrowUp", "Breakout");
      if (markers.length) {
        // markers must be sorted ascending by time (lightweight-charts requirement)
        markers.sort((a, b) => a.time - b.time);
        // lightweight-charts v5 removed `series.setMarkers()` — markers are now
        // attached via the dedicated `createSeriesMarkers(series, markers)` API.
        createSeriesMarkers(candle, markers);
      }
    }

    // Volume pane
    const volSeries = chart.addSeries(HistogramSeries, {
      color: palette.volume,
      priceFormat: { type: "volume" },
      priceScaleId: "",
    });
    volSeries.priceScale().applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
    volSeries.setData(
      bars.map((b) => ({
        time: b.time,
        value: b.volume || 0,
        color: b.close >= b.open ? `${palette.up}66` : `${palette.down}66`,
      })),
    );

    // ─────────────────────────────────────────────────────────────────
    // Optional technical indicators (user-selectable from the toolbar)
    // ─────────────────────────────────────────────────────────────────
    // Strategy:
    //   - "Overlay" indicators (EMA / BB / VWAP / Donchian / SuperTrend /
    //     Pivots / Fib) live on the main price scale.
    //   - "Sub-pane" indicators (RSI / MACD / ADX / ATR / OBV) live in
    //     dedicated panes (paneIndex 1, 2, …) created on demand.
    //   - Heavy compute is done in `../lib/indicators.js` outside this
    //     effect's hot path; we just call the helpers and feed the
    //     `{time, value}` arrays into Lightweight Charts series.
    //   - Volume is always rendered above; user can also explicitly
    //     toggle other overlays.
    const sel = new Set(indicators || []);
    // Per-indicator params merged over defaults. Each key matches the
    // toolbar key in StockDetailModal. Any non-finite user value falls back
    // to the default so transient input states (mid-typing) can't crash
    // lightweight-charts.
    const DEFAULTS = {
      ema20: { period: 20 },
      ema50: { period: 50 },
      ema200: { period: 200 },
      bb: { period: 20, mult: 2 },
      donchian: { period: 20 },
      supertrend: { period: 10, mult: 3 },
      rsi: { period: 14, overbought: 70, oversold: 30 },
      macd: { fast: 12, slow: 26, signal: 9 },
      adx: { period: 14 },
      atr: { period: 14 },
    };
    const userP = indicatorParams || {};
    const P = {};
    for (const k of Object.keys(DEFAULTS)) {
      const merged = { ...DEFAULTS[k] };
      const user = userP[k] || {};
      for (const f of Object.keys(merged)) {
        const v = Number(user[f]);
        if (Number.isFinite(v) && v > 0) merged[f] = v;
      }
      P[k] = merged;
    }
    if (sel.size) {
      const overlayLine = (color, lineWidth = 1.5, dashed = false) => ({
        color, lineWidth,
        priceLineVisible: false,
        lastValueVisible: false,
        lineStyle: dashed ? 2 : 0,
      });
      const fmtBars = (data, lineOpts) => {
        const s = chart.addSeries(LineSeries, lineOpts);
        s.setData(data);
        return s;
      };

      if (sel.has("ema20")) fmtBars(ind.emaSeries(bars, P.ema20.period), { ...overlayLine("#10b981"), title: `EMA ${P.ema20.period}` });
      if (sel.has("ema50")) fmtBars(ind.emaSeries(bars, P.ema50.period), { ...overlayLine("#3b82f6"), title: `EMA ${P.ema50.period}` });
      if (sel.has("ema200")) fmtBars(ind.emaSeries(bars, P.ema200.period), { ...overlayLine("#f59e0b", 2), title: `EMA ${P.ema200.period}` });
      if (sel.has("vwap")) fmtBars(ind.vwap(bars), { ...overlayLine("#a78bfa", 1.5, true), title: "VWAP" });

      if (sel.has("bb")) {
        const bb = ind.bollingerBands(bars, P.bb.period, P.bb.mult);
        fmtBars(bb.upper, { ...overlayLine("#06b6d4", 1), title: `BB upper (${P.bb.period}, ${P.bb.mult})` });
        fmtBars(bb.middle, { ...overlayLine("#06b6d4", 1, true), title: "BB mid" });
        fmtBars(bb.lower, { ...overlayLine("#06b6d4", 1), title: "BB lower" });
      }

      if (sel.has("donchian")) {
        const dc = ind.donchian(bars, P.donchian.period);
        fmtBars(dc.upper, { ...overlayLine("#f97316", 1), title: `Donchian U (${P.donchian.period})` });
        fmtBars(dc.lower, { ...overlayLine("#f97316", 1), title: "Donchian L" });
      }

      if (sel.has("supertrend")) {
        const st = ind.supertrend(bars, P.supertrend.period, P.supertrend.mult);
        if (st.up.length) fmtBars(st.up, { ...overlayLine("#10b981", 2), title: `SuperTrend (${P.supertrend.period},${P.supertrend.mult})` });
        if (st.down.length) fmtBars(st.down, { ...overlayLine("#ef4444", 2), title: "SuperTrend" });
      }

      if (sel.has("pivots")) {
        const p = ind.pivotPoints(bars);
        if (p) {
          const pl = (price, color, title) =>
            candle.createPriceLine({ price, color, lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title });
          pl(p.pp, "#fbbf24", "PP");
          pl(p.r1, "#10b981", "R1"); pl(p.r2, "#10b981", "R2");
          pl(p.s1, "#ef4444", "S1"); pl(p.s2, "#ef4444", "S2");
        }
      }

      if (sel.has("fib")) {
        const fib = ind.fibRetrace(bars);
        if (fib) {
          fib.forEach((f) =>
            candle.createPriceLine({
              price: f.price,
              color: "#a78bfa",
              lineWidth: 1,
              lineStyle: f.lvl === 0 || f.lvl === 1 ? 0 : 2,
              axisLabelVisible: true,
              title: `Fib ${(f.lvl * 100).toFixed(1)}%`,
            }),
          );
        }
      }

      // ── Sub-pane indicators ────────────────────────────────────────
      let nextPane = 1;
      const newPane = () => {
        chart.addPane();
        return nextPane++;
      };
      if (sel.has("rsi")) {
        const idx = newPane();
        const s = chart.addSeries(LineSeries, { color: "#a78bfa", lineWidth: 1.5, priceLineVisible: false, title: `RSI ${P.rsi.period}` }, idx);
        s.setData(ind.rsi(bars, P.rsi.period));
        // User-configurable overbought / oversold guide lines (default 70 / 30).
        s.createPriceLine({ price: P.rsi.overbought, color: "#ef4444", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: String(P.rsi.overbought) });
        s.createPriceLine({ price: P.rsi.oversold, color: "#10b981", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: String(P.rsi.oversold) });
      }
      if (sel.has("macd")) {
        const idx = newPane();
        const m = ind.macd(bars, P.macd.fast, P.macd.slow, P.macd.signal);
        const hist = chart.addSeries(HistogramSeries, { priceFormat: { type: "price", precision: 4, minMove: 0.0001 }, priceLineVisible: false }, idx);
        hist.setData(m.histogram);
        const macdLine = chart.addSeries(LineSeries, { color: "#06b6d4", lineWidth: 1.5, priceLineVisible: false, title: `MACD (${P.macd.fast},${P.macd.slow},${P.macd.signal})` }, idx);
        macdLine.setData(m.macd);
        const sigLine = chart.addSeries(LineSeries, { color: "#f97316", lineWidth: 1.5, priceLineVisible: false, title: "Signal" }, idx);
        sigLine.setData(m.signal);
      }
      if (sel.has("adx")) {
        const idx = newPane();
        const s = chart.addSeries(LineSeries, { color: "#fbbf24", lineWidth: 1.5, priceLineVisible: false, title: `ADX ${P.adx.period}` }, idx);
        s.setData(ind.adx(bars, P.adx.period));
        s.createPriceLine({ price: 25, color: palette.text, lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "25" });
      }
      if (sel.has("atr")) {
        const idx = newPane();
        const s = chart.addSeries(LineSeries, { color: "#06b6d4", lineWidth: 1.5, priceLineVisible: false, title: `ATR ${P.atr.period}` }, idx);
        s.setData(ind.atr(bars, P.atr.period));
      }
      if (sel.has("obv")) {
        const idx = newPane();
        const s = chart.addSeries(AreaSeries, {
          topColor: "rgba(167,139,250,0.35)",
          bottomColor: "rgba(167,139,250,0.05)",
          lineColor: "#a78bfa",
          lineWidth: 1.5,
          priceLineVisible: false,
          priceFormat: { type: "volume" },
          title: "OBV",
        }, idx);
        s.setData(ind.obv(bars));
      }
    }

    chart.timeScale().fitContent();

    // Expose chart instance + main candle series to the parent (used by
    // the drawing-tools overlay for pixel↔price/time coordinate translation).
    if (typeof onReady === "function") {
      onReady({ chart, candleSeries: candle, container: el });
    }

    const ro = new ResizeObserver(() => {
      chart.applyOptions({ width: el.clientWidth });
    });
    ro.observe(el);

    // ─── Crosshair → OHLCV legend ──────────────────────────────────────
    // Subscribe to crosshair moves, lookup the hovered bar and update the
    // legend state. We use a Map for O(1) time-indexed lookup since
    // lightweight-charts gives us the time, not the bar index.
    const barByTime = new Map();
    for (let i = 0; i < bars.length; i += 1) barByTime.set(bars[i].time, { ...bars[i], _i: i });
    const initialBar = bars[bars.length - 1];
    setHovered({ ...initialBar, _i: bars.length - 1 });
    const onCrosshairMove = (param) => {
      if (!param || !param.time) {
        // Cursor left the chart — show latest bar
        setHovered({ ...initialBar, _i: bars.length - 1 });
        return;
      }
      const b = barByTime.get(param.time);
      if (b) setHovered(b);
    };
    chart.subscribeCrosshairMove(onCrosshairMove);

    return () => {
      ro.disconnect();
      try { chart.unsubscribeCrosshairMove(onCrosshairMove); } catch { /* */ }
      if (typeof onReady === "function") onReady(null);
      chart.remove();
      el.replaceChildren();
    };
  }, [bars, ema10, entry, stopLoss, target, height, theme, cupOverlay, emaCrossOverlay, goldenCrossOverlay, niftyBars, sectorBars, sectorName, indicators, indicatorParams, srLevels, onReady]);

  // Compute % change vs previous bar's close — TradingView shows
  // "change vs previous bar". Falls back to today's bar open if no prev.
  const prevBar = hovered?._i > 0 ? bars[hovered._i - 1] : null;
  const refClose = prevBar?.close ?? hovered?.open;
  const dPrice = hovered ? hovered.close - refClose : 0;
  const dPct = refClose ? (dPrice / refClose) * 100 : 0;
  const up = dPrice >= 0;
  const fmtPrice = (n) => (n == null ? "—" : n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const fmtVol = (n) => {
    if (n == null) return "—";
    if (n >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
    if (n >= 1e5) return `${(n / 1e5).toFixed(2)} L`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(2)} K`;
    return String(n);
  };

  return (
    <div className="w-full relative">
      <div
        data-testid="candle-chart"
        ref={ref}
        className="w-full border border-subtle rounded-sm bg-app"
        style={{ height }}
      />
      {hovered && (
        <div
          data-testid="chart-ohlc-legend"
          className="absolute top-2 left-2 px-2.5 py-1.5 bg-surface/85 backdrop-blur-sm border border-subtle rounded-sm font-mono text-[11px] leading-tight pointer-events-none select-none z-10 shadow-lg"
        >
          {/* Stock identity */}
          {(symbol || companyName) && (
            <div className="flex items-baseline gap-1.5 mb-0.5">
              {symbol && <span className="text-primary-fg font-semibold">{symbol.replace(".NS", "")}</span>}
              {companyName && (
                <span className="text-muted-fg text-[9px] uppercase tracking-widest truncate max-w-[180px]">
                  · {companyName}
                </span>
              )}
            </div>
          )}
          {/* OHLC row + change pill */}
          <div className="flex items-baseline gap-2">
            <span className="text-muted-fg text-[9px] uppercase">O</span>
            <span className="text-primary-fg tabular-nums">{fmtPrice(hovered.open)}</span>
            <span className="text-muted-fg text-[9px] uppercase ml-1">H</span>
            <span className="text-primary-fg tabular-nums">{fmtPrice(hovered.high)}</span>
            <span className="text-muted-fg text-[9px] uppercase ml-1">L</span>
            <span className="text-primary-fg tabular-nums">{fmtPrice(hovered.low)}</span>
            <span className="text-muted-fg text-[9px] uppercase ml-1">C</span>
            <span className={"tabular-nums font-semibold " + (up ? "text-pos" : "text-neg")}>
              {fmtPrice(hovered.close)}
            </span>
            <span className={"tabular-nums " + (up ? "text-pos" : "text-neg")}>
              {up ? "+" : ""}{dPrice.toFixed(2)} ({up ? "+" : ""}{dPct.toFixed(2)}%)
            </span>
          </div>
          {/* Volume row */}
          <div className="flex items-baseline gap-1.5 mt-0.5">
            <span className="text-muted-fg text-[9px] uppercase tracking-widest">Vol</span>
            <span className="text-secondary-fg tabular-nums">{fmtVol(hovered.volume)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
